import AnswerFormatter from "./answerFormatter";
import { EXCLUDED_KEYS, EXCLUDED_SUB_KEYS } from "./constants";
import { buildExcludedSections } from "./sectionRules";
import {
  addEditButton,
  createElement,
  replacePlaceholders,
  sanitizeQuestionTitle,
} from "./dom";

const REVIEW_CONTAINER_SELECTOR = ".review-content";
const POLL_INTERVAL_MS = 500;

export default class ReviewRenderer {
  constructor(survey, options = {}) {
    this.survey = survey;
    this.answerFormatter = new AnswerFormatter(survey);
    this.excludedSections = buildExcludedSections(survey);
    this.readOnly = options.readOnly || false;
  }

  init() {
    this.intervalId = setInterval(() => {
      const container = document.querySelector(REVIEW_CONTAINER_SELECTOR);
      if (!container) {
        return;
      }

      clearInterval(this.intervalId);
      this.renderInto(container);
    }, POLL_INTERVAL_MS);
  }

  renderInto(container) {
    container.classList.add("flex", "flex-col", "gap-5");

    if (!this.survey || !this.survey.pages) {
      return;
    }

    this.survey.visiblePages.forEach((page, index) => {
      const pageElement = this.buildPageElement(page, index);
      if (pageElement) {
        container.appendChild(pageElement);
      }
    });

    if (this.shouldRenderErrorBanner()) {
      container.appendChild(this.buildErrorBanner());
    }
  }

  buildPageElement(page, pageNumber) {
    if (!page.elements || this.excludedSections.has(page.name)) {
      return null;
    }

    const wrapperDiv = createElement("div", "flex flex-col p-5 gap-5 rounded-lg");
    const hasErrors = page.hasErrors(false);
    const title = createElement("h2", "text-xl2");
    title.innerText = page.name;
    wrapperDiv.appendChild(title);

    if (hasErrors) {
      wrapperDiv.classList.add("bg-amber-50", "text-amber-700");
      wrapperDiv.appendChild(this.buildPageErrorBanner(pageNumber));
    } else {
      wrapperDiv.classList.add(
        "bg-forest-50",
        "light:bg-navy-50",
        "text-forest-600",
        "light:text-navy-700"
      );
    }

    page.elements.forEach((question) => {
      const qType = typeof question.getType === "function" ? question.getType() : question.type;
      if (!this.shouldRenderQuestion(question)) {
        return;
      }
      if (qType === "paneldynamic" && Array.isArray(question.panels)) {
        // Get answers for this paneldynamic question
        const panelAnswers = this.survey.getValue(question.name) || [];
        question.panels.forEach((panel, panelIdx) => {
          const panelDiv = createElement("div", "panel-dynamic flex flex-col gap-3 p-3 rounded-md bg-slate-50");
          let hasRenderedAny = false;
          // Optionally add a panel title/label
          if (panel.title) {
            const panelTitle = createElement("h4", "font-semibold text-base");
            panelTitle.innerText = replacePlaceholders(
              sanitizeQuestionTitle(this.survey, panel.title),
              this.survey.data
            );
            panelDiv.appendChild(panelTitle);
          }
          // Render each question in the panel, passing panel answer context
          if (Array.isArray(panel.elements)) {
            const originalGetValue = this.survey.getValue;
            this.survey.getValue = (name) => {
              if (panelAnswers[panelIdx] && name in panelAnswers[panelIdx]) {
                return panelAnswers[panelIdx][name];
              }
              return originalGetValue.call(this.survey, name);
            };
            try {
              panel.elements.forEach((nestedQuestion) => {
                // Only render if shouldRenderQuestion returns true
                if (this.shouldRenderQuestion(nestedQuestion)) {
                  const before = panelDiv.childNodes.length;
                  this.appendQuestion(nestedQuestion, panelDiv);
                  if (panelDiv.childNodes.length > before) {
                    hasRenderedAny = true;
                  }
                }
              });
            } finally {
              this.survey.getValue = originalGetValue;
            }
          }
          // Only append panelDiv if at least one nested question rendered content
          if (hasRenderedAny) {
            wrapperDiv.appendChild(panelDiv);
          }
        });
      } else {
        this.appendQuestion(question, wrapperDiv);
      }
    });

    if (!this.readOnly) {
      addEditButton(this.survey, wrapperDiv, pageNumber, { isError: hasErrors });
    }
    return wrapperDiv;
  }

  appendQuestion(question, wrapperDiv) {
    if (!this.shouldRenderQuestion(question)) {
      return;
    }

    const questionDiv = this.createQuestionDiv(question);
    const answerWrapperDiv = createElement("div", "grid grid-cols-2 gap-5");
    let hasRenderedAnswer = false;

    if (question.templateElements) {
      question.templateElements.forEach((subQuestion) => {
        hasRenderedAnswer = this.appendSubQuestion(question, subQuestion, answerWrapperDiv) || hasRenderedAnswer;
      });
    } else if (question.items) {
      question.items.forEach((subQuestion) => {
        hasRenderedAnswer = this.appendSubQuestion(question, subQuestion, answerWrapperDiv) || hasRenderedAnswer;
      });
    } else if (this.answerFormatter.render(answerWrapperDiv, question) !== null) {
      hasRenderedAnswer = true;
    }

    if (hasRenderedAnswer) {
      questionDiv.appendChild(answerWrapperDiv);
      wrapperDiv.appendChild(questionDiv);
    }
  }

  appendSubQuestion(question, subQuestion, wrapperDiv) {
    if (EXCLUDED_SUB_KEYS.includes(subQuestion.name)) {
      return false;
    }

    const subQuestionDiv = createElement("div", "sub-question");

    if (subQuestion.title && !subQuestion.items) {
      const title = createElement("h4", "font-bold");
      title.innerText = sanitizeQuestionTitle(this.survey, subQuestion.title);
      subQuestionDiv.appendChild(title);
    }

    const answer = this.answerFormatter.render(subQuestionDiv, question, subQuestion.name);
    if (answer !== null) {
      wrapperDiv.appendChild(subQuestionDiv);
      return true;
    }

    return false;
  }

  createQuestionDiv(question) {
    const isMultiQuestion = Boolean(question.items);
    const questionDiv = createElement("div", isMultiQuestion ? "flex flex-col gap-2" : "flex flex-col");
    const questionTitle = isMultiQuestion ? createElement("h3", "text-lg") : createElement("h4", "font-bold");
    questionTitle.innerText = replacePlaceholders(
      sanitizeQuestionTitle(this.survey, question.title),
      this.survey.data
    );
    questionDiv.appendChild(questionTitle);
    return questionDiv;
  }

  shouldRenderQuestion(question) {
    return !EXCLUDED_KEYS.includes(question.name) && question.visible !== false;
  }

  shouldRenderErrorBanner() {
    return this.survey.visiblePages.some(
      (page) => !this.excludedSections.has(page.name) && page.hasErrors(false)
    );
  }

  buildErrorBanner() {
    const banner = createElement(
      "div",
      "self-stretch p-3 bg-yellow-50 rounded-lg outline outline-1 outline-yellow-800 inline-flex justify-start items-center gap-3"
    );
    const msg = createElement("span", "block text-lg font-semibold text-yellow-800");
    msg.innerText = "Required information still needs to be filled out before you can continue.";
    banner.appendChild(msg);
    return banner;
  }

  buildPageErrorBanner(pageNumber) {
    const banner = createElement(
      "div",
      "flex items-center justify-between gap-4 p-4 rounded-md bg-amber-100 text-yellow-800 border border-yellow-800"
    );
    const msg = createElement("span");
    msg.innerText = "Required information is missing from this section";
    banner.appendChild(msg);
    if (!this.readOnly) {
      addEditButton(this.survey, banner, pageNumber, { isError: true });
    }
    return banner;
  }
}
